﻿import React, { useEffect, useState } from 'react';
import 'chart.js/auto';
import 'chartjs-plugin-datalabels';

import { Box, Grid, Typography, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useTranslation } from 'react-i18next';
import { Chart } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import { getDailyParameters } from '../../../components/Cards/DailyParameters/cardFunctions';
import { InsoleUsageData } from '../../../components/Cards/InsoleUsageData';
import IconLoader from '../../../components/IconLoader';
import Scrollable from '../../../components/Scrollable';
import {
  insoleUsageToHours,
  minutesToHours,
} from '../../../helpers/utilityFunctions/utilityFunctions';
import { PassiveParameterTypeEnum } from '../../../model/passive/enum/passiveParameterTypeEnum';
import { selectedDailyParameterData } from '../../../redux/selectors/dailyParametersSelectors';
import InsoleUsageDetailContent from './InsoleUsageDetailContent';

interface Props {
  patientId?: string;
  showCharts?: boolean;
}

const ContentContainer = styled(Box)(({ theme }) => ({
  ...theme.typography.body2,
  width: '100%',
  padding: '20px 40px',
  boxSizing: 'border-box',
}));

const StatCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

export const ActivityTrackerDetailContent: React.FC<Props> = ({ patientId, showCharts = false }) => {
  const { t } = useTranslation('activity');
  const dailyParameterData = useSelector(selectedDailyParameterData);
  const [activityTrackerData, setActivityTrackerData] = useState<InsoleUsageData>();

  useEffect(() => {
    if (dailyParameterData) {
      setActivityTrackerData(
        getDailyParameters(
          dailyParameterData,
          PassiveParameterTypeEnum.InsoleUsage
        ) as InsoleUsageData
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyParameterData]);

  const values = [
    Number(activityTrackerData?.other),
    Number(activityTrackerData?.stand),
    Number(activityTrackerData?.sit),
    Number(activityTrackerData?.walk),
  ];

  const colors = ['#b3b3b3', '#cce4f4', '#68d1df', '#3f838a'];

  const legendData = [
    {
      component: <IconLoader name="WalkingMan" />,
      label: t('legend.walk'),
      value: values[3],
      color: colors[3],
    },
    {
      component: <IconLoader name="SittingMan" />,
      label: t('legend.sit'),
      value: values[2],
      color: colors[2],
    },
    {
      component: <IconLoader name="StandingMan" />,
      label: t('legend.stand'),
      value: values[1],
      color: colors[1],
    },
    {
      component: <IconLoader name="JumpingMan" />,
      label: t('legend.other'),
      value: values[0],
      color: colors[0],
    },
  ];

  const pieData = {
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        borderColor: colors,
        border: 1,
      },
    ],
  };

  const pieOptions: any = {
    responsive: true,
    maintainAspectRatio: true,
    layout: {
      padding: 80,
    },
    plugins: {
      datalabels: {
        textAlign: 'center',
        align: 'end',
        anchor: 'end',
        color: '#101820',
        font: {
          family: ['"Montserrat"', 'Open Sans'].join(','),
          size: 20,
        },
        offset: 3,
        padding: 2,
        formatter: (val: number, context: any) => {
          const displayArray = [false, false, false, false];

          const total = values.reduce((acc, val) => acc + val, 0);

          for (let i = 0; i < values.length; i++) {
            if (values[i] > (total / 100) * 10) {
              displayArray[i] = true;
            }
          }

          const typeOfActionByOrder = [
            t('chartActions.other'),
            t('chartActions.standing'),
            t('chartActions.sitting'),
            t('chartActions.walking'),
          ][context.dataIndex];
          return val > 0 && displayArray[context.dataIndex]
            ? minutesToHours(val) + `\n ${typeOfActionByOrder}`
            : '';
        },
      },
    },
  };

  const hoursUsage = activityTrackerData
    ? insoleUsageToHours(activityTrackerData).split(' ')
    : ['0'];

  const totalMinutes = values.reduce((acc, val) => acc + val, 0);

  return (
    <Scrollable>
      <ContentContainer>
        <Typography variant="h6" gutterBottom>
          {t('distribution.heading')}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          {t('distribution.subtitle')}
        </Typography>

        <Grid container spacing={3}>
          {/* Summary Card */}
          <Grid size={12}>
            <StatCard>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {hoursUsage[0]}
                <Typography component="span" variant="body1" sx={{ ml: 1 }}>
                  {t('usingInsoles', {
                    duration: hoursUsage.slice(1).join(' '),
                  })}
                </Typography>
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {dailyParameterData && dailyParameterData[0] && dailyParameterData[0].date
                  ? new Date(dailyParameterData[0].date).toLocaleDateString()
                  : t('noDate')}
              </Typography>
            </StatCard>
          </Grid>

          {/* Chart Section */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard>
              <Box
                sx={{
                  height: '500px',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Chart
                  type="pie"
                  data={pieData}
                  options={pieOptions}
                  plugins={[ChartDataLabels]}
                />
              </Box>
            </StatCard>
          </Grid>

          {/* Activity Breakdown */}
          <Grid size={{ xs: 12, md: 6 }}>
            <StatCard>
              <Typography variant="h6" gutterBottom>
                {t('breakdown')}
              </Typography>
              {legendData.map(({ component, label, value, color }, index) => {
                const percentage = totalMinutes > 0
                  ? ((value / totalMinutes) * 100).toFixed(1)
                  : '0';
                return (
                  <Box
                    key={label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px',
                      marginBottom: '12px',
                      borderRadius: '8px',
                      backgroundColor: `${color}20`,
                      borderLeft: `4px solid ${color}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {component}
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {label}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {minutesToHours(value)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {percentage}%
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </StatCard>
          </Grid>
        </Grid>

        {showCharts && (
          <>
            {/* Divider between sections */}
            <Divider sx={{ my: 6 }} />

            {/* Time-Series Charts Section */}
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              {t('trends.title')}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {t('trends.subtitle')}
            </Typography>

            <InsoleUsageDetailContent patientId={patientId} />
          </>
        )}
      </ContentContainer>
    </Scrollable>
  );
};

export default ActivityTrackerDetailContent;

